import { z } from 'zod';
/**
 * Corpus ingest pipeline. Lives at:
 *   POST /api/admin/ingest/validate  — schema-check, return preview + warnings
 *   POST /api/admin/ingest/run       — SSE; runs ingest, streams progress,
 *                                       writes an `ingestRuns/{id}` doc
 *   GET  /api/admin/ingest/runs      — list past runs (newest first)
 *   GET  /api/admin/ingest/runs/:id  — one run with full error log
 *
 * Cross-tenant by design: the Neo4j graph is shared across orgs (it IS the
 * product), so all four endpoints are `superAdmin`-only and `ingestRuns`
 * docs carry `orgId: 'platform'` — the same convention `audit/*` uses for
 * platform-level events.
 *
 * Every node and relationship written by an ingest run carries an
 * `ingest_run_id` Neo4j property so future tooling can attribute or roll
 * back a specific run. The default mode is `merge` (additive, idempotent
 * via `MERGE`); `replace` is the explicit destructive option that wipes
 * the graph first — kept available for the bootstrap CLI use case but the
 * UI requires explicit confirmation.
 */
// ─── Corpus shape ────────────────────────────────────────────────────────
//
// Matches `data/parsi_subgraph.json` and the legacy ingest CLI. Edge shape
// is intentionally permissive (`catchall`) because real corpora carry
// arbitrary domain props — `start`, `end`, `year`, `confidence`, etc. — that
// flow through to Neo4j as relationship properties.
export const ExpectedEdge = z
    .object({
    type: z.string().optional(),
    predicate: z.string().optional(),
    subject: z.string().optional(),
    target: z.string().optional(),
    object: z.string().optional(),
})
    .catchall(z.unknown());
export const ExpectedRelationship = z
    .object({
    subject: z.string().min(1),
    predicate: z.string().min(1),
    object: z.string().min(1),
})
    .catchall(z.unknown());
export const CorpusDocument = z.object({
    id: z.string().min(1),
    type: z.string().optional(),
    title: z.string().optional(),
    text: z.string().optional(),
    year: z.number().int().optional(),
    expected_nodes: z.array(z.string().min(1)).optional(),
    expected_edges: z.array(ExpectedEdge).optional(),
    expected_relationships: z.array(ExpectedRelationship).optional(),
});
/**
 * Hard caps protect the synchronous ingest path from blowing past Cloud
 * Functions' 540s timeout and from OOM-ing the function instance. The
 * web UI rejects oversize files client-side; the server enforces these
 * as well (defense in depth).
 *
 * Sizing notes:
 *   - 50 MB of JSON parses to ~150–200 MB of V8 objects (3–4× expansion).
 *     The function's global memory is 1 GiB (`apps/functions/src/index.ts`)
 *     to clear that payload with margin.
 *   - Real FEC scrapings (e.g. donor-indexed identities for an entire
 *     watchlist) commonly produce 30–45k contribution Documents per
 *     20–25 MB JSON; the 50k document cap matches what the byte cap
 *     actually admits. A pathological corpus with very wide edge
 *     fanouts can still time out before finalizing; in that case
 *     either split the file or use the CLI (`pnpm ingest --org-id <slug>`),
 *     which has no payload cap and no timeout.
 *   - For corpora >50 MB or >50k documents, the storage-backed async
 *     ingest path is the right answer (TBD).
 */
export const MAX_INGEST_DOCUMENTS = 50_000;
export const MAX_INGEST_BYTES = 50 * 1024 * 1024; // 50 MB
/**
 * Optional geo coordinates attached to a named entity. Used by the
 * `/map` analyst surface — entities without a known primary location
 * are simply omitted from the map view, so the field stays optional
 * across the whole corpus.
 *
 * `place` is the human-readable label ("Washington DC") shown in the
 * marker popover; the name is the Neo4j entity-name key.
 */
export const EntityLocation = z.object({
    name: z.string().min(1),
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
    place: z.string().max(120).optional(),
});
export const Corpus = z.object({
    corpus_name: z.string().optional(),
    source_document: z.string().optional(),
    scope_note: z.string().optional(),
    documents: z.array(CorpusDocument).min(1).max(MAX_INGEST_DOCUMENTS),
    /** Optional location overlay — see {@link EntityLocation}. Names that
     *  don't match an entity in the documents block are silently skipped
     *  by ingest. */
    entity_locations: z.array(EntityLocation).optional(),
});
// ─── Ingest mode + run records ───────────────────────────────────────────
export const IngestMode = z.enum(['merge', 'replace']);
export const IngestRunStatus = z.enum([
    'queued',
    'running',
    'completed',
    'partial',
    'failed',
    'canceled',
]);
export const IngestRunPhase = z.enum([
    'validate',
    'constraints',
    'clear',
    'documents',
    'demo_seeds',
    'finalize',
]);
/**
 * One row in the per-run error log. `phase` tells the user where it broke
 * (validation vs. document write vs. finalize). `docId` is set when the
 * error is per-document; missing for whole-run failures.
 */
export const IngestRunError = z.object({
    phase: IngestRunPhase,
    message: z.string(),
    docId: z.string().optional(),
    detail: z.record(z.string(), z.unknown()).optional(),
    at: z.string(),
});
/**
 * Persisted record of a single ingest attempt. Stored at
 * `ingestRuns/{runId}` — server-only-write, superAdmin-read (rules).
 *
 * `errors` is capped to {@link MAX_RUN_ERRORS} on write so a malformed
 * 10k-doc corpus can't blow past Firestore's 1MB doc size limit;
 * `errorCount` always reflects the true total.
 */
export const IngestRun = z.object({
    id: z.string(),
    // Run-level ownership scope — distinct from the Neo4j `orgId` stamped
    // on Documents/edges/Candidates. `'platform'` here means a superAdmin
    // pushed against the shared corpus; those Neo4j writes are stamped
    // `orgId='system'`. An org-slug here means an org-admin pushed via
    // /api/org/ingest; those Neo4j writes carry the same org slug. Reads
    // and rule gates use this field to decide who can see / cancel /
    // rollback the run, NOT to filter Neo4j queries (that uses the Neo4j
    // `orgId`).
    orgId: z.string(),
    status: IngestRunStatus,
    mode: IngestMode,
    // Source-file fingerprint
    fileName: z.string().optional(),
    fileSize: z.number().int().nonnegative().optional(),
    fileHash: z.string().optional(),
    corpusName: z.string().optional(),
    sourceDocument: z.string().optional(),
    // Timing
    startedAt: z.string(),
    finishedAt: z.string().optional(),
    durationMs: z.number().int().nonnegative().optional(),
    // Actor
    startedBy: z.string(),
    startedByEmail: z.string().optional(),
    // Counts
    documentCount: z.number().int().nonnegative(),
    documentsProcessed: z.number().int().nonnegative(),
    nodesWritten: z.number().int().nonnegative(),
    edgesWritten: z.number().int().nonnegative(),
    errorCount: z.number().int().nonnegative(),
    warningCount: z.number().int().nonnegative(),
    errors: z.array(IngestRunError),
    // Cancellation. Set by `POST /admin/ingest/runs/:id/cancel`. The service
    // polls this between documents (cross-instance signal) AND honors a
    // module-level AbortController in the same instance.
    cancelRequested: z.boolean().optional(),
    cancelRequestedAt: z.string().optional(),
    cancelRequestedBy: z.string().optional(),
    // Rollback. Set by `POST /admin/ingest/runs/:id/rollback` — stamping is
    // done by the server after the destructive Cypher executes; the run
    // doc's `status` stays whatever it was at finalize time so the history
    // remains honest, but the UI keys off these fields to render a
    // "rolled back" badge.
    rolledBackAt: z.string().optional(),
    rolledBackBy: z.string().optional(),
    rolledBackByEmail: z.string().optional(),
    rolledBackNodeCount: z.number().int().nonnegative().optional(),
    rolledBackEdgeCount: z.number().int().nonnegative().optional(),
});
export const MAX_RUN_ERRORS = 200;
// ─── Validate (preview) ───────────────────────────────────────────────────
export const IngestWarning = z.object({
    docId: z.string().optional(),
    field: z.string().optional(),
    message: z.string(),
});
/**
 * The shape of the file the user is uploading.
 *
 * - `canonical-corpus` — body's `corpus` already matches the {@link Corpus}
 *   Zod schema. Server validates it directly. This is the legacy format
 *   used by the demo `data/parsi_subgraph.json`.
 * - `fec-contributions` — body's `corpus` is the raw FEC contributions
 *   JSON: `{ metadata, contributions: [...] }`. Each contribution becomes
 *   one document with donor (Person), employer (Organization), and
 *   committee (Organization) entities + `CONTRIBUTED_TO` and (when present)
 *   `EMPLOYED_BY` edges.
 * - `irs990-officers` — body's `corpus` is a CSV string with columns
 *   `org_ein, org_name, tax_year, source, name, title, compensation, hours`.
 *   Each row becomes a document with Person + Organization entities and
 *   an `OFFICER_OF` edge.
 * - `irs990-grants` — body's `corpus` is a CSV string with columns
 *   `grantor_ein, grantor_name, tax_year, source, recipient, recipient_ein,
 *   amount, purpose`. Each row becomes a document with grantor + recipient
 *   Organization entities and a `GRANTED_TO` edge.
 *
 * Server adapters live at `apps/functions/src/ingest/adapters/` and
 * normalize every supported format into the canonical {@link Corpus} shape
 * before {@link runIngest} runs. The canonical-corpus path is a passthrough.
 */
export const SourceFormat = z.enum([
    'canonical-corpus',
    'fec-contributions',
    'fec-identities',
    'irs990-officers',
    'irs990-grants',
]);
/**
 * Which formats the adapter expects as a raw CSV string vs as a parsed
 * JSON value. The CLI uses this to decide how to read the file off
 * disk; the web UI uses it to set the `<input accept="...">` filter.
 * Single source of truth so both surfaces stay aligned when a new
 * format is added.
 */
const CSV_FORMATS = new Set([
    'irs990-officers',
    'irs990-grants',
]);
export function isCsvFormat(format) {
    return CSV_FORMATS.has(format);
}
export const ValidateIngestRequest = z.object({
    /** Raw payload — interpreted by the server's adapter for `sourceFormat`.
     *  For `canonical-corpus` this must match the {@link Corpus} schema; for
     *  CSV formats it's a UTF-8 string of the file contents. */
    corpus: z.unknown(),
    sourceFormat: SourceFormat.default('canonical-corpus'),
    /** Optional SHA-256 hex of the source file — enables "already imported" detection. */
    fileHash: z.string().max(128).optional(),
});
/**
 * Summary of a prior run that matched the uploaded file's hash. The full
 * `IngestRun` would bloat the response; this keeps just enough to render
 * a "you already imported this file" warning card.
 */
export const PriorIngestRunSummary = z.object({
    id: z.string(),
    startedAt: z.string(),
    finishedAt: z.string().optional(),
    status: IngestRunStatus,
    mode: IngestMode,
    fileName: z.string().optional(),
    startedBy: z.string(),
    startedByEmail: z.string().optional(),
    documentsProcessed: z.number().int().nonnegative(),
});
export const ValidateIngestResponse = z.object({
    ok: z.literal(true),
    documentCount: z.number().int().nonnegative(),
    expectedNodeCount: z.number().int().nonnegative(),
    expectedEdgeCount: z.number().int().nonnegative(),
    expectedRelationshipCount: z.number().int().nonnegative(),
    preview: z.object({
        corpusName: z.string().optional(),
        sourceDocument: z.string().optional(),
        sampleEntities: z.array(z.string()),
        sampleEdges: z.array(z.object({
            subject: z.string(),
            type: z.string(),
            object: z.string(),
        })),
    }),
    warnings: z.array(IngestWarning),
    /**
     * Prior runs that imported the same file (matched by `fileHash`). Only
     * populated when the caller provided a hash. Empty = first time we're
     * seeing this file. Non-empty = the user should know re-running will
     * upsert entities rather than create duplicates.
     */
    priorRuns: z.array(PriorIngestRunSummary).default([]),
});
// ─── Run (SSE) ────────────────────────────────────────────────────────────
export const StartIngestRequest = z.object({
    /** Raw payload — see {@link SourceFormat} for the per-format shape.
     *  Server runs the matching adapter, validates the result against
     *  {@link Corpus}, then hands it to runIngest. */
    corpus: z.unknown(),
    sourceFormat: SourceFormat.default('canonical-corpus'),
    mode: IngestMode.default('merge'),
    fileName: z.string().max(500).optional(),
    fileSize: z.number().int().nonnegative().optional(),
    fileHash: z.string().max(128).optional(),
});
/**
 * SSE wire format for `/api/admin/ingest/run`. Canonical order:
 *   run_started → phase(validate|constraints|clear|documents)…
 *   → document* (and document_failed* interspersed)
 *   → progress* (every N docs)
 *   → phase(demo_seeds|finalize) → complete
 * `error` may arrive at any point and terminates the stream (the run doc
 * still gets a final `failed` write).
 */
export const IngestSseEvent = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('run_started'),
        runId: z.string(),
        startedAt: z.string(),
        totalDocuments: z.number().int().nonnegative(),
        mode: IngestMode,
    }),
    z.object({
        type: z.literal('phase'),
        phase: IngestRunPhase,
        label: z.string(),
    }),
    z.object({
        type: z.literal('document'),
        docId: z.string(),
        index: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
        nodes: z.number().int().nonnegative(),
        edges: z.number().int().nonnegative(),
    }),
    z.object({
        type: z.literal('document_failed'),
        docId: z.string(),
        index: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
        message: z.string(),
    }),
    z.object({
        type: z.literal('progress'),
        processed: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
        nodesWritten: z.number().int().nonnegative(),
        edgesWritten: z.number().int().nonnegative(),
        errorCount: z.number().int().nonnegative(),
    }),
    z.object({ type: z.literal('complete'), run: IngestRun }),
    z.object({
        type: z.literal('canceled'),
        /** The full final run doc — same shape `complete` carries, with status='canceled'. */
        run: IngestRun,
    }),
    z.object({ type: z.literal('error'), message: z.string() }),
]);
// ─── List + read ──────────────────────────────────────────────────────────
export const ListIngestRunsResponse = z.object({
    runs: z.array(IngestRun),
});
// ─── Diff (preview existing vs. new before committing) ───────────────────
/**
 * Computed by `POST /api/admin/ingest/preview-diff` — shows how much of the
 * uploaded corpus already exists in the graph. Empties the upsert framing:
 * `existingEntities` will upsert (same node), `newEntities` will create new
 * nodes. For edges: `existingEdges` is a no-op (MERGE finds the edge),
 * `newEdges` will create new relationships.
 *
 * Counts are over UNIQUE (subject, type, object) edge tuples and unique
 * entity names — so a corpus with 500 docs that all reference "Trita Parsi"
 * counts that name once.
 */
export const IngestDiffStats = z.object({
    totalEntities: z.number().int().nonnegative(),
    newEntities: z.number().int().nonnegative(),
    existingEntities: z.number().int().nonnegative(),
    totalEdges: z.number().int().nonnegative(),
    newEdges: z.number().int().nonnegative(),
    existingEdges: z.number().int().nonnegative(),
    /** First few names that will be created — gives the user concrete examples. */
    sampleNewEntities: z.array(z.string()).max(8),
    /** First few names that already exist — confirms upsert behavior. */
    sampleExistingEntities: z.array(z.string()).max(8),
});
export const PreviewDiffRequest = z.object({
    corpus: Corpus,
});
export const PreviewDiffResponse = IngestDiffStats;
// ─── Rollback ────────────────────────────────────────────────────────────
/**
 * Result of `POST /api/admin/ingest/runs/:id/rollback`. Reports how much
 * was deleted from Neo4j; the run doc itself is preserved (history stays
 * honest) but stamped with `rolledBackAt` + `rolledBackBy`.
 */
// ─── Error codes (server → client) ───────────────────────────────────────
/**
 * Stable identifiers the server returns in `{ error: <code>, detail: ... }`
 * envelopes for the ingest endpoints. Shared so the client can branch on
 * a typed union instead of comparing raw strings, and so adding a new code
 * forces the UI to handle it.
 */
export const INGEST_ERROR_CODES = [
    'bad_body',
    'payload_too_large',
    'ingest_already_running',
    'not_running',
    'still_running',
    'already_rolled_back',
    'graph_unavailable',
    'not_found',
];
export const IngestErrorCode = z.enum(INGEST_ERROR_CODES);
export const RollbackIngestResponse = z.object({
    runId: z.string(),
    deletedNodes: z.number().int().nonnegative(),
    deletedEdges: z.number().int().nonnegative(),
});
